import { useContext, useEffect, useState } from "react"
import { genericEntityService } from "../../../services/genericEntityService";
import { M_Button, M_Header, NavigateContext, LoadingContext } from "../../../M_Components";
import { useTranslation } from "react-i18next";
import { EntityList } from "./EntityList";
import { EntityEditForm } from "./EntityEditForm";
import './adminPage.css';
import { FieldForm } from "./fields/FieldForm";
import { EntityConfiguration } from "./configurations/entityConfiguration";
import { ConfigurationsAddButton } from "./configurations/configurationsAddButton";
import { FieldTypeEnum } from "../../../enums/fieldTypeEnum";
import { refresherSignal } from "../menu/refresher-signal";
import { Dialog } from 'primereact/dialog';
import { ItemFormPreview } from "../entity/ItemFormPreview";
import { useGenericEntityContext } from "../context/genericEntityContext";
import { AddCircleOutlineOutlined } from '@mui/icons-material';

export const PAGE_STATE = {
    None: 0,
    EntityEdit: 1,
    EntityConfiguration: 2
}

export const ITEM_TYPE = {
    Field: 0,
    Menu: 1,
    Preview: 2
}

const DEFAULT_FIELD_VALUES = {
    fieldId: null,
    code: null,
    captions: {},
    defaultValue: null,
    isMandatory: false,
    isUnique: false,
    type: null,
    description: null
};

const DEFAULT_FIELD_FORM = {
    field: DEFAULT_FIELD_VALUES,
    typeSpecific: {},
    errors: {
        code: null,
        type: null,
        captions: null,
        description: null
    },
    typeSpecificErrors: {
        textSize: null,
        secondaryEntityId: null,
        secondaryEntityFields: null,
        decimalDigits: null,
        range: null,
        defaultValue: null,
    }
};

const MAX_NUMBER_OF_FIELDS = 1010;

export const AdminPage = ({ entityId, onCreateTab }) => {
    const [t, i18n] = useTranslation();
    const navCtx = useContext(NavigateContext);
    const loadingCtx = useContext(LoadingContext);
    const { hasPermission } = useGenericEntityContext();

    const [entities, setEntities] = useState([]);
    const [pageState, setPageState] = useState({
        state: entityId === undefined ? PAGE_STATE.None : PAGE_STATE.EntityConfiguration,
        entityId: entityId,
        type: null
    })
    const [selectedItem, setSelectedItem] = useState(null);
    const [canSaveField, setCanSaveField] = useState(null);
    const [displayDialog, setDisplayDialog] = useState(null);
    const [isButtonClicked, setIsButtonClicked] = useState(false);
    const [attemptedFieldSave, setAttemptedFieldSave] = useState(false);

    const selectedEntity = pageState.entityId === null ? null :
        entities.find(e => e.EntityId === pageState.entityId) ?? null;

    const createTab = (url, label) => {
        if (navCtx?.newTab) {
            navCtx.newTab(url, label)
        } else if (onCreateTab) {
            onCreateTab(url, label)
        }
    }

    const openEntityTab = (entity) => {
        const label = entity.Captions[i18n.language] || entity.Code
        createTab(`genericentity/entity/${entity.EntityId}`, label)
    }

    const fetchEntitites = () => {
        genericEntityService.getEntities()
            .then(function (response) {
                setEntities([...response.data]);
            })
            .catch(function (error) {
                handleHttpError(error);
            });
    }

    useEffect(() => {
        fetchEntitites()
    }, []);

    useEffect(() => {
        if (pageState === PAGE_STATE.None) {
            fetchEntitites()
        }
    }, [pageState.state]);

    useEffect(() => {
        canSave();
    }, [selectedItem?.form])

    const setPage = (state, entityId) => {
        setPageState({
            entityId: entityId,
            state: state
        });
    }

    const setItem = (itemId, type, form, canEdit) => {
        setSelectedItem({
            itemId: itemId,
            type: type,
            form: form,
            canEdit: canEdit
        })
    }

    const handleHttpError = (error) => {
        const errors = error?.response?.data?.errors ?? ["ServerError"];
        setDisplayDialog(errors);
        setIsButtonClicked(false);
    }

    const handleEntityEdit = (id) => setPage(PAGE_STATE.EntityEdit, id);

    const handleToggleEntityEdit = () => {
        if (pageState.state === PAGE_STATE.EntityEdit) {
            setPage(PAGE_STATE.None, null);
        } else {
            setPage(PAGE_STATE.EntityEdit, null);
        }
    };

    const handleDeleteEntity = (entityId) => {
        if (!window.confirm(t("NgeConfirmDelete"))) return;

        genericEntityService.deleteEntityById(entityId)
            .then(function (response) {
                if (pageState.entityId === entityId) {
                    setPage(PAGE_STATE.None, null)
                }
                fetchEntitites();
                refresherSignal.value = true;
                loadingCtx.toastMessages('success', t("NgeDeleteSuccess"));
            })
            .catch(function (error) {
                handleHttpError(error);
            });
    }

    const changeSaveState = (state) => setCanSaveField(state);

    const handleEntityClick = (entityId) => {
        setPage(PAGE_STATE.EntityConfiguration, entityId);
        setSelectedItem(null);
    }

    const handleFieldSave = () => {
        setAttemptedFieldSave(true);

        if (!canSaveField) {
            return;
        }

        setIsButtonClicked(true);

        const cleanCaptions = {};
        if (selectedItem.form.field.captions) {
            Object.keys(selectedItem.form.field.captions).forEach(key => {
                if (key && key !== 'undefined' && key !== 'null' && selectedItem.form.field.captions[key]) {
                    cleanCaptions[key] = selectedItem.form.field.captions[key];
                }
            });
        }

        const fieldData = {
            ...selectedItem.form.field,
            ...selectedItem.form.typeSpecific,
            captions: cleanCaptions
        };

        if (selectedItem.form.field.fieldId !== null) {
            genericEntityService.updateEntityField(pageState.entityId, selectedItem.form.field.fieldId, fieldData)
                .then(function (response) {
                    setSelectedItem(null);
                    fetchEntitites();
                    setIsButtonClicked(false);
                    loadingCtx.toastMessages('success', t("NgeSaveSuccess"));
                })
                .catch(function (error) {
                    handleHttpError(error);
                });
        } else {
            genericEntityService.createEntityField(pageState.entityId, fieldData)
                .then(function (response) {
                    setSelectedItem(null);
                    fetchEntitites();
                    setIsButtonClicked(false);
                    loadingCtx.toastMessages('success', t("NgeCreateSuccess"));
                })
                .catch(function (error) {
                    handleHttpError(error);
                });
        }
    }

    const handleAddFieldClick = () => {
        setAttemptedFieldSave(false);
        setPageState({
            ...pageState,
            type: ITEM_TYPE.Field
        })
        setItem(0, ITEM_TYPE.Field, DEFAULT_FIELD_FORM, true);
        setCanSaveField(false);
    }

    const handleAddItemClick = (type) => {
        setPageState({
            ...pageState,
            type: ITEM_TYPE.Field
        })
        switch (type) {
            case ITEM_TYPE.Field:
                setItem(0, ITEM_TYPE.Field, DEFAULT_FIELD_FORM, true);
                return;
            case ITEM_TYPE.Menu:
                setItem(0, ITEM_TYPE.Menu, {}, true);
                return;
            default:
                return;
        }
    }

    const handleTreeSelection = (item, type) => {
        if (item.fieldId === 0 || item.FieldId === 0) {
            return;
        }

        switch (type) {
            case ITEM_TYPE.Field:
                const fieldData = {
                    fieldId: item.FieldId || item.fieldId,
                    code: item.Code || item.code,
                    captions: item.Captions || item.captions || {},
                    defaultValue: item.DefaultValue || item.defaultValue,
                    isMandatory: item.IsMandatory ?? item.isMandatory ?? false,
                    isUnique: item.HasIndex ?? item.isUnique ?? false,
                    type: item.Type || item.type,
                    description: item.Description || item.description
                };

                const typeSpecificProps = {
                    textSize: item.TextSize || item.textSize,
                    secondaryEntityId: item.SecondaryEntityId || item.secondaryEntityId,
                    secondaryEntityFields: item.SecondaryEntityFields || item.secondaryEntityFields,
                    decimalDigits: item.DecimalDigits || item.decimalDigits,
                    minValue: item.MinValue || item.minValue,
                    maxValue: item.MaxValue || item.maxValue,
                    dropdownItems: item.DropdownItems || item.dropdownItems,
                    defaultValue: item.DefaultValue || item.defaultValue
                };

                Object.keys(typeSpecificProps).forEach(key => {
                    if (typeSpecificProps[key] === undefined) {
                        delete typeSpecificProps[key];
                    }
                });

                setItem(
                    fieldData.fieldId,
                    ITEM_TYPE.Field,
                    {
                        ...DEFAULT_FIELD_FORM,
                        field: fieldData,
                        typeSpecific: typeSpecificProps
                    },
                    false
                );
                break;
            default:
                return;
        }

        setPageState({
            ...pageState,
            type: ITEM_TYPE.Field
        })
    }

    const handleCancelFieldEdit = () => {
        setSelectedItem(null);
    }

    const handleFormChange = (form) => {
        setSelectedItem({
            ...selectedItem,
            form: form
        })
    }

    const handleFieldEdit = () => {
        setSelectedItem({
            ...selectedItem,
            canEdit: true
        })
    }

    const handleFieldDelete = () => {
        if (!window.confirm(t("NgeConfirmDelete"))) return;

        genericEntityService.deleteEntityField(pageState.entityId, selectedItem.itemId)
            .then(function (response) {
                setSelectedItem(null);
                fetchEntitites();
                loadingCtx.toastMessages('success', t("NgeDeleteSuccess"));
            })
            .catch(function (error) {
                handleHttpError(error);
            });
    }

    const handleFieldSort = (sortedCodes) => {
        const orderMap = {};
        sortedCodes.forEach((code, index) => {
            orderMap[code] = index;
        });
        const sortedFields = selectedEntity.Fields.sort((a, b) => orderMap[a.Code] - orderMap[b.Code]);
        genericEntityService.reorderEntityFields(pageState.entityId, {
            ...selectedEntity,
            fields: sortedFields
        })
            .then(function (response) {
                fetchEntitites();
                loadingCtx.toastMessages('success', t("NgeSaveSuccess"));
            })
            .catch(function (error) {
                handleHttpError(error);
            });
    }

    const handlePreview = () => {
        setPageState({
            ...pageState,
            type: ITEM_TYPE.Preview
        })
        setSelectedItem(null);
    }

    const canSave = () => {
        const field = {
            ...selectedItem?.form?.field,
            ...selectedItem?.form?.typeSpecific
        };

        if (selectedItem === null) {
            return;
        }

        const hasCodeError = selectedItem?.form?.errors?.code !== null;
        const hasTypeError = selectedItem?.form?.errors?.type !== null;
        const hasCaptionsError = selectedItem?.form?.errors?.captions !== null;
        const hasDescriptionError = selectedItem?.form?.errors?.description !== null;

        const hasTypeSpecificError = Object.values(selectedItem?.form?.typeSpecificErrors ?? {}).some(error => error !== null);

        if (hasCodeError || hasTypeError || hasCaptionsError || hasDescriptionError || hasTypeSpecificError) {
            setCanSaveField(false);
            return;
        }

        if (!field.code || field.code.length === 0) {
            setCanSaveField(false);
            return;
        }

        if (field.type === null || field.type === undefined) {
            setCanSaveField(false);
            return;
        }

        if (!field.captions || Object.keys(field.captions).length === 0) {
            setCanSaveField(false);
            return;
        }

        switch (field.type) {
            case FieldTypeEnum.Text:
                if (!field.textSize || field.textSize <= 0) {
                    setCanSaveField(false);
                    return;
                }
                break;
            case FieldTypeEnum.Relation:
                if (!field.secondaryEntityId || !field.secondaryEntityFields || field.secondaryEntityFields.length === 0) {
                    setCanSaveField(false);
                    return;
                }
                break;
            case FieldTypeEnum.Dropdown:
                if (!field.dropdownItems || field.dropdownItems.length === 0) {
                    setCanSaveField(false);
                    return;
                }
                break;
        }

        setCanSaveField(true);
    }

    return (
        <div className="ge-admin-container">
            {/* Entity List Column */}
            <div className="ge-admin-column ge-admin-entities">
                <div className="ge-admin-header">
                    <h4 className="ge-admin-title">{t("Entity")}</h4>
                    {hasPermission('canCreateEntity') && (
                        <M_Button
                            tooltip={t("NgeCreate")}
                            icon={<AddCircleOutlineOutlined style={{ color: 'var(--color-green-light)' }} />}
                            onClick={handleToggleEntityEdit}
                        />
                    )}
                </div>
                <div className="ge-admin-content">
                    {entities?.length === 0 ? (
                        <div className="ge-admin-empty">
                            <span>{t("NgeNoEntitiesCreated")}</span>
                        </div>
                    ) : (
                        <EntityList
                            entities={entities}
                            handleEdit={handleEntityEdit}
                            handleItemClick={handleEntityClick}
                            handleDelete={handleDeleteEntity}
                            selectedEntityId={pageState.entityId}
                            isAdmin={true}
                            isConfiguration={true}
                            canEdit={hasPermission('canEditEntity')}
                            canDelete={hasPermission('canDeleteEntity')}
                        />
                    )}
                </div>
            </div>

            {/* Configuration Column */}
            {pageState.state === PAGE_STATE.EntityConfiguration && selectedEntity && (
                <div className="ge-admin-column ge-admin-config">
                    <div className="ge-admin-header">
                        <h4 className="ge-admin-subtitle">{selectedEntity.Code}</h4>
                        <div className="ge-admin-header-actions">
                            <ConfigurationsAddButton
                                onAddClickCallback={handleAddItemClick}
                                isEnabled={selectedEntity.Fields.length < MAX_NUMBER_OF_FIELDS}
                            />
                            <M_Button
                                tooltip={t("NgePreview")}
                                label={t("NgePreview")}
                                icon={<i className='pi pi-eye'></i>}
                                onClick={handlePreview}
                            />
                        </div>
                    </div>
                    <div className="ge-admin-content">
                        <EntityConfiguration
                            entity={selectedEntity}
                            selectedItem={selectedItem}
                            handleAddFieldClick={handleAddFieldClick}
                            fieldSelectCallback={handleTreeSelection}
                            isInsertion={selectedItem?.itemId === 0}
                            dragDropCallback={handleFieldSort}
                        />
                    </div>
                </div>
            )}

            {/* Details Column */}
            {pageState.state === PAGE_STATE.EntityConfiguration && selectedEntity && pageState.type === ITEM_TYPE.Field && selectedItem && (
                <div className="ge-admin-column ge-admin-details">
                    <div className="ge-admin-header">
                        <h4 className="ge-admin-subtitle">{selectedItem.itemId === 0 ? t("NgeNewField") : selectedEntity.Fields.find(f => f.FieldId === selectedItem.itemId)?.Code ?? ""}</h4>
                        <div className="ge-admin-header-actions">
                            {selectedItem.canEdit ? (
                                <>
                                    <M_Button
                                        tooltip={t("NgeSave")}
                                        label={t("NgeSave")}
                                        icon={<i className='pi pi-check'></i>}
                                        disabled={!canSaveField || isButtonClicked}
                                        onClick={handleFieldSave}
                                    />
                                    <M_Button
                                        tooltip={t("NgeCancel")}
                                        label={t("NgeCancel")}
                                        icon={<i className='pi pi-times'></i>}
                                        onClick={handleCancelFieldEdit}
                                    />
                                </>
                            ) : (
                                <>
                                    <M_Button
                                        tooltip={t("NgeEdit")}
                                        label={t("NgeEdit")}
                                        icon={<i className='pi pi-pencil iconTable'></i>}
                                        onClick={handleFieldEdit}
                                    />
                                    <M_Button
                                        tooltip={t("NgeDelete")}
                                        label={t("NgeDelete")}
                                        icon={<i className='pi pi-trash iconTable'></i>}
                                        onClick={handleFieldDelete}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                    <div className="ge-admin-content">
                        <FieldForm
                            existingFields={selectedEntity.Fields}
                            form={selectedItem.form}
                            changeCallback={handleFormChange}
                            canEdit={selectedItem.canEdit}
                            canSaveField={{ canSaveField: canSaveField, setCanSaveField: changeSaveState }}
                            attemptedSave={attemptedFieldSave}
                            isExtendedEntity={selectedEntity.ExtendedEntity !== null}
                        />
                    </div>
                </div>
            )}

            {/* Preview Column */}
            {pageState.state === PAGE_STATE.EntityConfiguration && selectedEntity && pageState.type === ITEM_TYPE.Preview && (
                <div className="ge-admin-column ge-admin-preview">
                    <div className="ge-admin-header">
                        <h4 className="ge-admin-subtitle">{t("NgePreview")}</h4>
                    </div>
                    <div className="ge-admin-content ge-preview-content">
                        <ItemFormPreview
                            fields={selectedEntity.Fields}
                            entityId={selectedEntity.EntityId}
                        />
                    </div>
                </div>
            )}

            {/* Entity Edit Full Width */}
            {pageState.state === PAGE_STATE.EntityEdit && (
                <div className="ge-admin-full-width">
                    <EntityEditForm
                        setPageState={setPageState}
                        entity={selectedEntity}
                        entities={entities}
                    />
                </div>
            )}

            {/* Error Dialog */}
            <Dialog
                visible={displayDialog !== null}
                modal
                header={t("NgeErrorHeader")}
                footer={
                    <M_Button
                        tooltip="Ok"
                        label="Ok"
                        icon={<i className='pi pi-check'></i>}
                        onClick={() => setDisplayDialog(null)}
                    />
                }
                onHide={() => setDisplayDialog(null)}
            >
                <p>
                    {displayDialog !== null && displayDialog?.map(e => t(`Nge${e}`)).join(", ")}
                </p>
            </Dialog>
        </div>
    )
}
